import { SxProps, Theme } from "@mui/material";

import {
  isPasswordBasedLoginAuthType,
  LoginAuthType,
  MuiPaletteType,
  TPasswordBasedLoginAuthType,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

/* eslint-disable @typescript-eslint/naming-convention -- MUI `sx` selector keys */

export const radioButtonLoginSx: SxProps<Theme> = {
  "& .MuiSvgIcon-root": {
    fontSize: 22,
  },
};

const nonProductionLoginAccentColorSx: SxProps<Theme> = {
  color: rydeColors.nonProductionNavbarOrangeHover,
  "&.Mui-checked": {
    color: rydeColors.nonProductionNavbarOrangeHover,
  },
};

export const getRadioButtonLoginSx = (
  isNonProductionLoginColors: boolean,
): SxProps<Theme> => ({
  ...radioButtonLoginSx,
  ...(isNonProductionLoginColors
    ? {
        "& .MuiRadio-root": nonProductionLoginAccentColorSx,
      }
    : {}),
});

export const buttonGreyBorderSx: SxProps<Theme> = {
  height: "42px",
  border: `1px solid ${rydeColors.greyBorderButton}`,
  transition:
    "box-shadow 0.22s ease, transform 0.22s ease, border-color 0.22s ease",
  boxShadow: "0 1px 2px rgba(49, 27, 146, 0.05)",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 4px 14px rgba(103, 58, 183, 0.12)",
  },
};

export const buttonLoginColor: { [key in LoginAuthType]?: MuiPaletteType } = {
  [LoginAuthType.Email]: "secondary",
  [LoginAuthType.Phone]: "info",
};

export const getButtonLoginColor = (
  loginType: LoginAuthType,
): MuiPaletteType => {
  return buttonLoginColor[loginType] ?? "primary";
};

// Mirrors production primary / secondary / info button hues when switching Username / Phone / Email.
const nonProductionLoginButtonColorsByAuthType: Record<
  TPasswordBasedLoginAuthType,
  {
    backgroundColor: string;
    hoverBackgroundColor: string;
    boxShadow: string;
    hoverBoxShadow: string;
  }
> = {
  [LoginAuthType.Username]: {
    backgroundColor: rydeColors.nonProductionNavbarOrangeHover,
    hoverBackgroundColor: rydeColors.nonProductionNavbarOrangeDark,
    boxShadow: "0 2px 10px rgba(204, 110, 0, 0.25)",
    hoverBoxShadow: "0 6px 18px rgba(153, 56, 0, 0.35)",
  },
  [LoginAuthType.Email]: {
    backgroundColor: rydeColors.nonProductionNavbarOrangeDark,
    hoverBackgroundColor: rydeColors.nonProductionNavbarOrangeDarker,
    boxShadow: "0 2px 10px rgba(153, 56, 0, 0.25)",
    hoverBoxShadow: "0 6px 18px rgba(153, 56, 0, 0.35)",
  },
  [LoginAuthType.Phone]: {
    backgroundColor: rydeColors.nonProductionNavbarOrange,
    hoverBackgroundColor: rydeColors.nonProductionNavbarOrangeHover,
    boxShadow: "0 2px 10px rgba(255, 138, 0, 0.25)",
    hoverBoxShadow: "0 6px 18px rgba(204, 110, 0, 0.35)",
  },
};

export const buttonLoginSx: SxProps<Theme> = (theme: Theme) => ({
  height: "42px",
  boxShadow: "0 2px 10px rgba(69, 39, 160, 0.2)",
  transition: theme.transitions.create(
    ["background-color", "color", "box-shadow", "transform"],
    {
      duration: 420,
      easing: theme.transitions.easing.easeInOut,
    },
  ),
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 6px 18px rgba(103, 58, 183, 0.35)",
  },
});

const nonProductionLoginButtonDisabledSx: SxProps<Theme> = {
  "&.Mui-disabled": {
    backgroundColor: "rgba(204, 110, 0, 0.5)",
    color: rydeColors.white,
  },
};

export const getButtonLoginSx = (
  isNonProductionLoginColors: boolean,
  loginType: LoginAuthType = LoginAuthType.Username,
): SxProps<Theme> => {
  if (!isNonProductionLoginColors) {
    return buttonLoginSx;
  }

  // Explicit `sx` colors override MUI `color` prop — callers use `color="inherit"` in non-production mode.
  const resolvedLoginType = isPasswordBasedLoginAuthType(loginType)
    ? loginType
    : LoginAuthType.Username;
  const colors = nonProductionLoginButtonColorsByAuthType[resolvedLoginType];

  return (theme: Theme) => ({
    ...(typeof buttonLoginSx === "function"
      ? buttonLoginSx(theme)
      : buttonLoginSx),
    backgroundColor: colors.backgroundColor,
    color: rydeColors.white,
    boxShadow: colors.boxShadow,
    transition: theme.transitions.create(
      ["background-color", "color", "box-shadow", "transform"],
      {
        duration: 420,
        easing: theme.transitions.easing.easeInOut,
      },
    ),
    "&:hover": {
      transform: "translateY(-1px)",
      backgroundColor: colors.hoverBackgroundColor,
      boxShadow: colors.hoverBoxShadow,
    },
    ...nonProductionLoginButtonDisabledSx,
  });
};

export const getLoginTextButtonSx = (
  isNonProductionLoginColors: boolean,
): SxProps<Theme> => {
  if (!isNonProductionLoginColors) {
    return buttonLoginSx;
  }

  return (theme: Theme) => ({
    ...(typeof buttonLoginSx === "function"
      ? buttonLoginSx(theme)
      : buttonLoginSx),
    color: rydeColors.nonProductionNavbarOrangeHover,
    height: "42px",
    // Reuse production shadow geometry; only tint changes for the OTP text button.
    boxShadow: "0 2px 10px rgba(153, 56, 0, 0.2)",
    "&:hover": {
      backgroundColor: "rgba(204, 110, 0, 0.08)",
      boxShadow: "0 6px 18px rgba(153, 56, 0, 0.35)",
      transform: "translateY(-1px)",
    },
  });
};
